import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, MessageCircle, ArrowUpRight } from "lucide-react";
import { PA_BASE, NAV_LINKS, waLink, BROKERAGE_DISCLAIMER, GROUP_NAME } from "../config";
import { EASE } from "./motion";

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 32);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      <header
        data-testid="pa-header"
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
          scrolled
            ? "bg-black/70 backdrop-blur-xl border-b border-white/10"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-5 sm:px-8 py-4">
          <Link to={PA_BASE} data-testid="pa-logo" className="group flex items-baseline gap-2 text-white">
            <span className="font-display text-xl tracking-tight">JS</span>
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/70 group-hover:text-white transition-colors duration-300">
              Private Aviation
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-7" data-testid="pa-nav">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `text-[13px] tracking-wide transition-colors duration-300 ${
                    isActive ? "text-white" : "text-white/55 hover:text-white"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to={`${PA_BASE}/charter#request`}
              data-testid="nav-request-charter-cta"
              className="ml-2 border border-white/30 px-5 py-2 text-[12px] uppercase tracking-[0.18em] text-white transition-colors duration-300 hover:bg-white hover:text-black"
            >
              Request a Charter
            </Link>
          </nav>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden text-white p-2"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-[#0A0A0A] flex flex-col justify-center px-8 lg:hidden"
          >
            <div className="space-y-5">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.5, ease: EASE }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="font-display text-3xl text-white/80 hover:text-white transition-colors duration-300"
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4, duration: 0.5, ease: EASE }}
                className="pt-6"
              >
                <Link
                  to={`${PA_BASE}/charter#request`}
                  data-testid="mobile-request-charter-cta"
                  className="inline-block border border-white/30 px-6 py-3 text-xs uppercase tracking-[0.2em] text-white"
                >
                  Request a Charter
                </Link>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

const Footer = () => (
  <footer data-testid="pa-footer" className="bg-[#0A0A0A] text-white border-t border-white/10">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-20">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <div className="flex items-baseline gap-2">
            <span className="font-display text-3xl tracking-tight">JS</span>
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/60">Private Aviation</span>
          </div>
          <p className="mt-6 max-w-sm text-sm leading-relaxed text-white/55">
            Private Aviation, Simplified. Global access to private aircraft, arranged around your schedule —
            through a worldwide network of independent aircraft operators.
          </p>
          <a
            href={waLink()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-whatsapp-cta"
            className="mt-8 inline-flex items-center gap-2 border border-white/25 px-5 py-2.5 text-[11px] uppercase tracking-[0.2em] text-white/80 transition-colors duration-300 hover:bg-white hover:text-black"
          >
            <MessageCircle size={14} /> WhatsApp a Charter Specialist
          </a>
        </div>
        <div className="md:col-span-3">
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/40">Explore</p>
          <ul className="mt-5 space-y-3 text-sm text-white/65">
            {NAV_LINKS.slice(0, 5).map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="hover:text-white transition-colors duration-300">
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div className="md:col-span-2">
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/40">Company</p>
          <ul className="mt-5 space-y-3 text-sm text-white/65">
            {NAV_LINKS.slice(5).map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="hover:text-white transition-colors duration-300">
                  {l.label}
                </Link>
              </li>
            ))}
            <li>
              <Link to="/" className="hover:text-white transition-colors duration-300">
                {GROUP_NAME}
              </Link>
            </li>
          </ul>
        </div>
        <div className="md:col-span-2">
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/40">Enquiries</p>
          <p className="mt-5 text-sm leading-relaxed text-white/65">
            Every enquiry is reviewed personally by our charter team.
          </p>
          <Link
            to={`${PA_BASE}/charter#request`}
            className="mt-4 inline-flex items-center gap-1 text-sm text-white hover:text-white/70 transition-colors duration-300"
          >
            Request a quote <ArrowUpRight size={14} />
          </Link>
        </div>
      </div>
      <div className="mt-16 border-t border-white/10 pt-8">
        <p className="max-w-3xl text-[11px] leading-relaxed text-white/35">{BROKERAGE_DISCLAIMER}</p>
        <p className="mt-4 text-[11px] text-white/35">
          © {new Date().getFullYear()} JS Private Aviation — part of the {GROUP_NAME} group.
        </p>
      </div>
    </div>
  </footer>
);

const FloatingWhatsApp = () => (
  <a
    href={waLink()}
    target="_blank"
    rel="noopener noreferrer"
    data-testid="floating-whatsapp-button"
    aria-label="WhatsApp a Charter Specialist"
    className="fixed bottom-6 right-6 z-40 flex items-center gap-2 rounded-full bg-white px-4 py-3 text-[11px] font-medium uppercase tracking-[0.15em] text-black shadow-[0_8px_30px_rgba(0,0,0,0.35)] transition-transform duration-300 hover:-translate-y-0.5"
  >
    <MessageCircle size={16} />
    <span className="hidden sm:inline">Charter Specialist</span>
  </a>
);

const ScrollManager = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      setTimeout(() => {
        document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
      }, 120);
    } else {
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [pathname, hash]);
  return null;
};

export const PALayout = () => {
  const { pathname } = useLocation();
  return (
    <div className="bg-[#F9F9F6] text-[#1A1A1A]">
      <ScrollManager />
      <Header />
      <AnimatePresence mode="wait">
        <motion.main
          key={pathname}
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5, ease: EASE }}
        >
          <Outlet />
        </motion.main>
      </AnimatePresence>
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
};
